import { createContext, useContext, useLayoutEffect, useSyncExternalStore } from "react";
import { PRIMARY_NAVIGATION_STRIP_FADE_INSET_PX } from "#primary-navigation-strip-fade-inset-context";

/** How far the bottom-tab fade reaches above the bar when no scene claims a different reach. */
export const PRIMARY_NAVIGATION_STRIP_DEFAULT_FADE_REACH_PX =
  PRIMARY_NAVIGATION_STRIP_FADE_INSET_PX;

type HostListener = (fadeReach: number) => void;

export type PrimaryNavigationStripFadeReachRegistry = {
  getCurrent: () => number;
  publish: (fadeReach: number) => () => void;
  subscribe: (listener: HostListener) => () => void;
};

/**
 * Lets a focused scene stretch the bottom-tab fade further up into itself, e.g. so it dissolves
 * content all the way up to a floating control instead of stopping at the default 9pt.
 * Reference-counted like the surface claim: overlapping mounts during a tab transition must not
 * drop a still-active claim. The most recent live claim wins.
 */
export function createPrimaryNavigationStripFadeReachRegistry(): PrimaryNavigationStripFadeReachRegistry {
  const entries = new Map<number, number>();
  let nextId = 0;
  const listeners = new Set<HostListener>();

  const getCurrent = (): number => {
    let current = PRIMARY_NAVIGATION_STRIP_DEFAULT_FADE_REACH_PX;
    for (const fadeReach of entries.values()) {
      current = fadeReach;
    }
    return current;
  };

  const notify = () => {
    const current = getCurrent();
    for (const listener of listeners) {
      listener(current);
    }
  };

  return {
    getCurrent,
    publish(fadeReach) {
      const id = ++nextId;
      entries.set(id, fadeReach);
      notify();
      return () => {
        if (entries.delete(id)) {
          notify();
        }
      };
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

const INERT_REGISTRY: PrimaryNavigationStripFadeReachRegistry = {
  getCurrent: () => PRIMARY_NAVIGATION_STRIP_DEFAULT_FADE_REACH_PX,
  publish: () => () => undefined,
  subscribe: () => () => undefined,
};

export const PrimaryNavigationStripFadeReachContext =
  createContext<PrimaryNavigationStripFadeReachRegistry>(INERT_REGISTRY);

/** Distance (px) the live bottom-tab fade currently reaches above the bar. */
export function usePrimaryNavigationStripFadeReachValue(): number {
  const registry = useContext(PrimaryNavigationStripFadeReachContext);
  return useSyncExternalStore(registry.subscribe, registry.getCurrent, registry.getCurrent);
}

/**
 * While `isActive` holds, claim `fadeReach` px for how far the shared bottom-tab fade extends
 * above the bar. Callers must already gate on navigation focus when inactive tabs stay mounted.
 */
export function usePrimaryNavigationStripFadeReach(fadeReach: number, isActive: boolean): void {
  const registry = useContext(PrimaryNavigationStripFadeReachContext);

  useLayoutEffect(() => {
    if (!isActive) {
      return;
    }
    return registry.publish(fadeReach);
  }, [fadeReach, isActive, registry]);
}
